import { useEffect, useState } from "react";

import { accounts, type AccountUser, type Accounts } from "@/lib/accounts";
import { isPreview } from "@/lib/preview";

/** Whether the studio should show the full app or the signed-out login screen.
 *
 * - `loading`   — still resolving provider config / restoring a session.
 * - `local`     — no auth provider configured (single-user local mode); the
 *                 app renders normally, there is no login.
 * - `signed-out`— a provider IS configured and there is no user; render the
 *                 dedicated login screen (no header / right rail).
 * - `signed-in` — a user is present; render the full app.
 *
 * This mirrors the auth contract in docs/architecture/frontend/app-shell-and-bundles.md: login
 * is a component gate, not a route. We drive off the provider's `onUser`
 * (fires immediately with the restored session) rather than a 401 so a
 * returning, signed-in user never flashes the login card. */
export type AuthStatus = "loading" | "local" | "signed-out" | "signed-in";

export interface AuthGate {
  status: AuthStatus;
  accs: Accounts | null;
  user: AccountUser | null;
}

export function useAuthGate(): AuthGate {
  const [accs, setAccs] = useState<Accounts | null>(null);
  const [user, setUser] = useState<AccountUser | null>(null);
  // `false` once the very first onUser callback (or "no provider") has landed.
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let unsub: (() => void) | undefined;

    // Dev preview (`?preview`): use an in-memory provider so the signed-out
    // screen renders with zero backend traffic (see lib/preview.ts). Loaded
    // lazily so the mock never enters the production path.
    const provider: Promise<Accounts | null> = isPreview()
      ? import("@/lib/preview").then((m) => m.mockAccounts())
      : accounts();

    void provider.then((a) => {
      if (cancelled) return;
      if (!a) {
        // No provider configured → local single-user mode.
        setAccs(null);
        setResolved(true);
        return;
      }
      setAccs(a);
      unsub = a.onUser((u) => {
        if (cancelled) return;
        setUser(u);
        setResolved(true);
      });
    });

    return () => {
      cancelled = true;
      unsub?.();
    };
  }, []);

  let status: AuthStatus;
  if (!resolved) status = "loading";
  else if (!accs) status = "local";
  else if (!user) status = "signed-out";
  else status = "signed-in";

  return { status, accs, user };
}
